import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { env } from '../../../../environments/environment';
import { AppConfigService } from '../app-config/app-config.service';
import { Observable, map } from 'rxjs';
import { Register, ResponseUser, EnteSuggestionResponse, AccreditamentoResponse, Accreditamento, AccreditamentoParams } from '../../../shared/models/utente';
import { IndirizzoSuggestion, IndirizzoSuggestionResponse, ProprietarioCollezione } from '../../../shared/models/wizardData';
import { ApiEnteProprietario } from '../../../shared/models/api-collection.models';


@Injectable({ providedIn: 'root' })
export class AuthServiceService {
  private http = inject(HttpClient);
  private oauth = inject(OAuthService);
  private appConfig = inject(AppConfigService);

  private urlUtente = `${env.api.baseUrlPublic}/register`;
  private myData = `${env.api.baseUrlProtected}/utente/me`;

  user = signal<ResponseUser | null>(null);
  iamOnlyUser = signal<boolean>(false);

  setUser(user: ResponseUser) {
    this.iamOnlyUser.set(false);
    return this.user.set(user);
  }

  private clearCookie(name: string, path: string, domain?: string): void {
    const baseCookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=${path}; SameSite=Lax`;
    document.cookie = domain ? `${baseCookie}; domain=${domain}` : baseCookie;
  }

  private clearClientCookies(): void {
    const cookieNames = document.cookie
      .split(';')
      .map((entry) => entry.split('=')[0].trim())
      .filter(Boolean);

    if (!cookieNames.length) return;

    const hostname = window.location.hostname;
    const hostParts = hostname.split('.');
    const domains: string[] = [hostname, `.${hostname}`];
    for (let i = 1; i < hostParts.length - 1; i++) {
      const domain = hostParts.slice(i).join('.');
      domains.push(domain, `.${domain}`);
    }

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const paths: string[] = ['/'];
    let composed = '';
    for (const part of pathParts) {
      composed += `/${part}`;
      paths.push(composed);
    }

    for (const name of cookieNames) {
      for (const path of paths) {
        this.clearCookie(name, path);
        for (const domain of domains) {
          this.clearCookie(name, path, domain);
        }
      }
    }
  }

  clearClientState(): void {
    this.user.set(null);
    this.iamOnlyUser.set(false);
    sessionStorage.clear();
    localStorage.clear();
    this.clearClientCookies();
  }

  hasAccessToken(): boolean {
    return !!this.oauth.getAccessToken();
  }

  logout(): void {
    // Read idToken BEFORE clearing localStorage — angular-oauth2-oidc reads
    // id_token from storage (not memory), so clearing first loses id_token_hint.
    // Without id_token_hint the IDP doesn't invalidate the SSO session.
    const idToken = this.oauth.getIdToken();
    this.clearClientState();

    const authConfig = this.appConfig.getAuthConfig();
    const url = new URL(authConfig.logoutUrl);
    if (idToken) {
      url.searchParams.set('id_token_hint', idToken);
    }
    url.searchParams.set('post_logout_redirect_uri', authConfig.post_logout_redirect_uri);
    window.location.href = url.toString();
  }

  logoutToRegister(): void {
    this.iamOnlyUser.set(true);
  }

  /** Email dall'identity token IAM (cultura.gov.it), se disponibile. */
  getIamEmail(): string | null {
    const claims = this.oauth.getIdentityClaims() as Record<string, unknown> | null;
    const email = claims?.['email'];
    return typeof email === 'string' && email.trim() ? email.trim() : null;
  }

  /**
   * Dopo verifica OTP: azzera lo stato "solo IAM" e riapre la sessione.
   * @returns 'home' se il token è ancora valido, altrimenti avvia il login OAuth.
   */
  completeRegistrationAfterOtp(): 'home' | 'login' {
    this.iamOnlyUser.set(false);
    sessionStorage.setItem('post_register_snackbar', 'true');
    if (this.hasAccessToken()) {
      return 'home';
    }
    this.login();
    return 'login';
  }

  login(): void {
    this.oauth.initLoginFlow(undefined, { prompt: 'login', max_age: '0' });
  }

  relogin(): void {
    this.clearClientState();
    this.oauth.initLoginFlow(undefined, { prompt: 'login', max_age: '0' });
  }

  register(data: Register): Observable<ResponseUser> {
    return this.http.post<ResponseUser>(`${this.urlUtente}`, data);
  }

  myProfile(): Observable<ResponseUser> {
    return this.http.get<ResponseUser>(`${this.myData}`);
  }

  verifyOtp(otp: string, mail: string): Observable<any> {
    return this.http.put<any>(`${env.api.baseUrlPublic}/verify-otp/${otp}/${mail}`, null);
  }

  resendOtp(mail: string): Observable<any> {
    return this.http.get<any>(`${env.api.baseUrlPublic}/resend-otp/${mail}`);
  }

  suggestionEnte(term: string): Observable<EnteSuggestionResponse> {
    const params = new HttpParams().set('term', term);
    return this.http.get<EnteSuggestionResponse>(`${env.api.baseUrlPublic}/ente-suggestion`, { params });
  }

  suggestionRuolo(term: string): Observable<any> {
    const params = new HttpParams().set('term', term);
    return this.http.get<any>(`${env.api.baseUrlPublic}/ruolo-suggestion`, { params });
  }

  /** Crea un nuovo ente tramite endpoint pubblico (usato dalla modale "nuovo ente" della register). */
  setupProprietario(data:ProprietarioCollezione):Observable<any>{
    return this.http.post<any>(`${env.api.baseUrlPublic}/ente`, data)
  }


  /** Autocomplete indirizzi tramite endpoint pubblico (usato nel form "nuovo ente" della register). */
  indirizzoSuggestion(term: string): Observable<IndirizzoSuggestion[]> {
    const params = new HttpParams().set('term', term);
    return this.http
      .get<IndirizzoSuggestionResponse>(`${env.api.baseUrlPublic}/indirizzo-suggestion`, { params })
      .pipe(map(res => res.elements ?? []));
  }

  getMicAuthState(data: AccreditamentoParams): Observable<AccreditamentoResponse> {
    let params = new HttpParams();

    if (data.paginazione) {
      if (data.paginazione.page !== undefined) {
        params = params.set('paginazione.page', data.paginazione.page.toString());
      }
      if (data.paginazione.totalPages !== undefined) {
        params = params.set('paginazione.totalPages', data.paginazione.totalPages.toString());
      }
      if (data.paginazione.totalElements !== undefined) {
        params = params.set('paginazione.totalElements', data.paginazione.totalElements.toString());
      }
      if (data.paginazione.totalElementsForPage !== undefined) {
        params = params.set('paginazione.totalElementsForPage', data.paginazione.totalElementsForPage.toString());
      }
    }

    if (data.order) {
      params = params.set('order', data.order);
    }
    if (data.orderField) {
      params = params.set('orderField', data.orderField);
    }
    if (data.flagAnnullato) {
      params = params.set('flagAnnullato', data.flagAnnullato);
    }

    return this.http.get<AccreditamentoResponse>(`${env.api.baseUrlProtected}/richiesta-accreditamento`, { params });
  }

}
